import { checkManifest } from "@obean/game-contract";
import type { GameModule } from "@obean/game-contract";

export interface GameRef {
  id: string;
  version: string;
}

export type AnyGame = GameModule<unknown, unknown, HTMLElement>;

/** The bundle for a pinned game version isn't loadable from this page. */
export class GameUnavailable extends Error {
  constructor(
    readonly ref: GameRef,
    reason: string,
  ) {
    super(`${ref.id}@${ref.version}: ${reason}`);
  }
}

/** Versioned, immutable bundle path (built by vite.config.ts). */
export const bundleUrl = (ref: GameRef) => `/games/${ref.id}/${ref.version}/client.js`;

export type Importer = (url: string) => Promise<unknown>;
const browserImport: Importer = (url) => import(/* @vite-ignore */ url);

/**
 * Loads exactly the pinned version of a game (ADR 0004: a match pins its game version) and checks
 * it against the game contract. Throws GameUnavailable when it can't.
 */
export async function loadGame(ref: GameRef, importer: Importer = browserImport): Promise<AnyGame> {
  let mod: unknown;
  try {
    mod = await importer(bundleUrl(ref));
  } catch (err) {
    throw new GameUnavailable(ref, `bundle didn't load (${String(err)})`);
  }
  const game = (mod as { default?: Partial<AnyGame> } | null)?.default;
  if (!game || typeof game.createClient !== "function" || typeof game.rules !== "object")
    throw new GameUnavailable(ref, "not a game bundle");
  const checked = checkManifest(game.manifest);
  if (!checked.ok) throw new GameUnavailable(ref, checked.errors.join("; "));
  if (checked.manifest.id !== ref.id || checked.manifest.version !== ref.version)
    throw new GameUnavailable(ref, `bundle is ${checked.manifest.id}@${checked.manifest.version}`);
  return game as AnyGame;
}

const RELOADED_FOR = "obean:reloaded-for";

/**
 * The reload path: a match asked for a game version this page can't load, usually because a deploy
 * moved on. Reload once to get the current build; returns false if this version already caused a
 * reload, so the caller shows an error instead of looping.
 */
export function reloadOnce(
  ref: GameRef,
  storage: Pick<Storage, "getItem" | "setItem"> = sessionStorage,
  reload: () => void = () => {
    location.reload();
  },
): boolean {
  const key = `${ref.id}@${ref.version}`;
  if (storage.getItem(RELOADED_FOR) === key) return false;
  storage.setItem(RELOADED_FOR, key);
  reload();
  return true;
}
